// services/pontosService.js
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc,
  collection,
  addDoc,
  query,
  where,
  orderBy,
  getDocs,
  serverTimestamp,
  increment,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { obterPontosPorReal } from './assinaturaService';

/**
 * Serviço de Pontos de Fidelidade
 * 
 * Regras:
 * - R$ 1,00 gasto = 1 ponto ganho (normal)
 * - R$ 1,00 gasto = 2 pontos ganhos (assinante)
 * - 100 pontos = R$ 1,00 de desconto
 */

// Configuração do programa de pontos
const PONTOS_POR_REAL_PADRAO = 1; // 1 ponto por R$ 1,00 (normal)
const PONTOS_PARA_REAL = 100; // 100 pontos = R$ 1,00

/**
 * Obtém o saldo de pontos do usuário
 * @param {string} userId - ID do usuário
 * @returns {Promise<{success: boolean, pontos: number, error?: string}>}
 */
export const getPontosSaldo = async (userId) => {
  try {
    if (!userId) {
      return { success: false, pontos: 0, error: 'Usuário não autenticado' };
    }

    const userRef = doc(db, 'usuarios', userId);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      const data = userSnap.data();
      const pontos = data.pontos || 0;
      return { success: true, pontos };
    } else {
      // Usuário não existe, criar com 0 pontos
      await setDoc(userRef, {
        pontos: 0,
        pontosTotalGanhos: 0,
        pontosTotalUsados: 0,
        createdAt: serverTimestamp()
      });
      return { success: true, pontos: 0 };
    }
  } catch (error) {
    console.error('❌ Erro ao obter saldo de pontos:', error);
    return { success: false, pontos: 0, error: error.message };
  }
};

/**
 * Adiciona pontos após uma compra
 * @param {string} userId - ID do usuário
 * @param {number} valorCompra - Valor total da compra (em reais)
 * @param {string} pedidoId - ID do pedido relacionado
 * @returns {Promise<{success: boolean, pontosGanhos: number, saldoAtual: number, error?: string}>}
 */
export const adicionarPontos = async (userId, valorCompra, pedidoId = null) => {
  try {
    console.log(`💰 [adicionarPontos] Iniciando - userId: ${userId}, valor: R$ ${valorCompra}, pedidoId: ${pedidoId}`);
    
    if (!userId) {
      console.error('❌ [adicionarPontos] userId não fornecido');
      return { success: false, error: 'Usuário não autenticado' };
    }

    if (!valorCompra || valorCompra <= 0) {
      console.error(`❌ [adicionarPontos] Valor inválido: ${valorCompra}`);
      return { success: false, error: 'Valor da compra inválido' };
    }

    // Verificar se o usuário tem assinatura (2 pontos por real) ou normal (1 ponto por real)
    const pontosConfig = await obterPontosPorReal(userId);
    const pontosPorReal = pontosConfig.pontosPorReal || PONTOS_POR_REAL_PADRAO;
    const isAssinante = pontosConfig.temBonus || false;

    // Calcular pontos ganhos (arredondado para baixo)
    const pontosGanhos = Math.floor(valorCompra * pontosPorReal);
    console.log(`💰 [adicionarPontos] Pontos calculados: ${pontosGanhos} (R$ ${valorCompra} × ${pontosPorReal}) ${isAssinante ? '✨ ASSINANTE' : ''}`);

    if (pontosGanhos <= 0) {
      console.error(`❌ [adicionarPontos] Nenhum ponto calculado`);
      return { success: false, error: 'Nenhum ponto a ser adicionado' };
    }

    console.log(`💰 [adicionarPontos] Buscando documento do usuário: usuarios/${userId}`);
    const userRef = doc(db, 'usuarios', userId);
    const userSnap = await getDoc(userRef);
    console.log(`💰 [adicionarPontos] Usuário existe? ${userSnap.exists()}`);

    if (userSnap.exists()) {
      const data = userSnap.data();
      const pontosAtuais = data.pontos || 0;
      const pontosTotalGanhos = (data.pontosTotalGanhos || 0) + pontosGanhos;
      const novosPontos = pontosAtuais + pontosGanhos;

      // Atualizar pontos do usuário
      console.log(`💰 [adicionarPontos] Atualizando documento usuarios/${userId}`);
      console.log(`💰 [adicionarPontos] Pontos atuais: ${pontosAtuais} → Novos: ${novosPontos}`);
      try {
        await updateDoc(userRef, {
          pontos: novosPontos,
          pontosTotalGanhos: pontosTotalGanhos,
          ultimaAtualizacaoPontos: serverTimestamp()
        });
        console.log(`✅ [adicionarPontos] Documento usuarios atualizado com sucesso`);
      } catch (updateError) {
        console.error(`❌ [adicionarPontos] Erro ao atualizar documento:`, updateError);
        console.error(`❌ [adicionarPontos] Código do erro:`, updateError.code);
        console.error(`❌ [adicionarPontos] Mensagem:`, updateError.message);
        throw updateError; // Propaga o erro
      }

      // Criar registro de transação
      console.log(`💰 [adicionarPontos] Criando transação em pontos_transacoes`);
      try {
        await addDoc(collection(db, 'pontos_transacoes'), {
          userId,
          tipo: 'ganho',
          pontos: pontosGanhos,
          pontosAntes: pontosAtuais,
          pontosDepois: novosPontos,
          pedidoId: pedidoId || null,
          descricao: `Pontos ganhos na compra de R$ ${valorCompra.toFixed(2)}`,
          valorCompra: valorCompra,
          createdAt: serverTimestamp()
        });
        console.log(`✅ [adicionarPontos] Transação criada com sucesso`);
      } catch (transacaoError) {
        console.error(`❌ [adicionarPontos] Erro ao criar transação:`, transacaoError);
        console.error(`❌ [adicionarPontos] Código do erro:`, transacaoError.code);
        console.error(`❌ [adicionarPontos] Mensagem:`, transacaoError.message);
        // Não propaga erro aqui - pontos já foram adicionados
      }

      console.log(`✅ ${pontosGanhos} pontos adicionados ao usuário ${userId}. Novo saldo: ${novosPontos}`);

      return {
        success: true,
        pontosGanhos,
        saldoAtual: novosPontos
      };
    } else {
      // Criar usuário com pontos iniciais
      console.log(`💰 [adicionarPontos] Usuário não existe, criando documento usuarios/${userId}`);
      try {
        await setDoc(userRef, {
          pontos: pontosGanhos,
          pontosTotalGanhos: pontosGanhos,
          pontosTotalUsados: 0,
          createdAt: serverTimestamp(),
          ultimaAtualizacaoPontos: serverTimestamp()
        });
        console.log(`✅ [adicionarPontos] Documento usuarios criado com sucesso`);
      } catch (createError) {
        console.error(`❌ [adicionarPontos] Erro ao criar documento:`, createError);
        console.error(`❌ [adicionarPontos] Código do erro:`, createError.code);
        console.error(`❌ [adicionarPontos] Mensagem:`, createError.message);
        throw createError;
      }

      // Criar registro de transação
      console.log(`💰 [adicionarPontos] Criando transação em pontos_transacoes (usuário novo)`);
      try {
        await addDoc(collection(db, 'pontos_transacoes'), {
        userId,
        tipo: 'ganho',
        pontos: pontosGanhos,
        pontosAntes: 0,
        pontosDepois: pontosGanhos,
        pedidoId: pedidoId || null,
        descricao: `Pontos ganhos na compra de R$ ${valorCompra.toFixed(2)}`,
        valorCompra: valorCompra,
        createdAt: serverTimestamp()
      });
        console.log(`✅ [adicionarPontos] Transação criada com sucesso (usuário novo)`);
      } catch (transacaoError) {
        console.error(`❌ [adicionarPontos] Erro ao criar transação:`, transacaoError);
        // Não propaga erro aqui - usuário e pontos já foram criados
      }

      return {
        success: true,
        pontosGanhos,
        saldoAtual: pontosGanhos
      };
    }
  } catch (error) {
    console.error('❌ Erro ao adicionar pontos:', error);
    console.error('❌ Stack trace:', error.stack);
    return { success: false, error: error.message || 'Erro desconhecido' };
  }
};

/**
 * Usa pontos para gerar desconto
 * @param {string} userId - ID do usuário
 * @param {number} pontosUsar - Quantidade de pontos a usar
 * @param {string} pedidoId - ID do pedido (opcional, para histórico)
 * @returns {Promise<{success: boolean, desconto: number, saldoAtual: number, error?: string}>}
 */
export const usarPontos = async (userId, pontosUsar, pedidoId = null) => {
  try {
    if (!userId) {
      return { success: false, error: 'Usuário não autenticado' };
    }

    // Validar pontos a usar (deve ser múltiplo de 100)
    if (pontosUsar < PONTOS_PARA_REAL || pontosUsar % PONTOS_PARA_REAL !== 0) {
      return { 
        success: false, 
        error: `Você deve usar múltiplos de ${PONTOS_PARA_REAL} pontos (mínimo ${PONTOS_PARA_REAL})` 
      };
    }

    const userRef = doc(db, 'usuarios', userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      return { success: false, error: 'Usuário não encontrado' };
    }

    const data = userSnap.data();
    const pontosAtuais = data.pontos || 0;

    if (pontosAtuais < pontosUsar) {
      return { 
        success: false, 
        error: `Você não tem pontos suficientes. Saldo atual: ${pontosAtuais} pontos` 
      };
    }

    // Calcular desconto
    const desconto = pontosUsar / PONTOS_PARA_REAL;
    const novosPontos = pontosAtuais - pontosUsar;
    const pontosTotalUsados = (data.pontosTotalUsados || 0) + pontosUsar;

    // Atualizar pontos do usuário
    await updateDoc(userRef, {
      pontos: novosPontos,
      pontosTotalUsados: pontosTotalUsados,
      ultimaAtualizacaoPontos: serverTimestamp()
    });

    // Criar registro de transação
    await addDoc(collection(db, 'pontos_transacoes'), {
      userId,
      tipo: 'uso',
      pontos: pontosUsar,
      pontosAntes: pontosAtuais,
      pontosDepois: novosPontos,
      pedidoId: pedidoId || null,
      descricao: `Uso de ${pontosUsar} pontos = R$ ${desconto.toFixed(2)} de desconto`,
      desconto: desconto,
      createdAt: serverTimestamp()
    });

    console.log(`✅ ${pontosUsar} pontos usados pelo usuário ${userId}. Desconto: R$ ${desconto.toFixed(2)}. Novo saldo: ${novosPontos}`);

    return {
      success: true,
      desconto,
      saldoAtual: novosPontos,
      pontosUsados: pontosUsar
    };
  } catch (error) {
    console.error('❌ Erro ao usar pontos:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Obtém o histórico de transações de pontos
 * @param {string} userId - ID do usuário
 * @param {number} limit - Limite de transações (padrão: 50)
 * @returns {Promise<{success: boolean, transacoes: Array, error?: string}>}
 */
export const getHistoricoPontos = async (userId, limit = 50) => {
  try {
    if (!userId) {
      return { success: false, transacoes: [], error: 'Usuário não autenticado' };
    }

    const q = query(
      collection(db, 'pontos_transacoes'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const transacoes = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      transacoes.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt)
      });
    });

    // Limitar resultados
    const transacoesLimitadas = transacoes.slice(0, limit);

    return { success: true, transacoes: transacoesLimitadas };
  } catch (error) {
    console.error('❌ Erro ao obter histórico de pontos:', error);
    return { success: false, transacoes: [], error: error.message };
  }
};

/**
 * Obtém os pontos ganhos hoje pelo usuário
 * @param {string} userId - ID do usuário
 * @returns {Promise<{success: boolean, pontos: number, error?: string}>}
 */
export const getPontosGanhosHoje = async (userId) => {
  try {
    if (!userId) {
      return { success: false, pontos: 0, error: 'Usuário não autenticado' };
    }

    // Obter início e fim do dia atual (UTC-3, horário de Brasília)
    const agora = new Date();
    const offset = -3 * 60; // UTC-3 em minutos
    const localTime = new Date(agora.getTime() + (offset - agora.getTimezoneOffset()) * 60000);
    
    const inicioDia = new Date(localTime);
    inicioDia.setHours(0, 0, 0, 0);
    
    const fimDia = new Date(localTime);
    fimDia.setHours(23, 59, 59, 999);

    // Converter para Timestamp do Firestore
    const inicioTimestamp = Timestamp.fromDate(inicioDia);
    const fimTimestamp = Timestamp.fromDate(fimDia);

    const q = query(
      collection(db, 'pontos_transacoes'),
      where('userId', '==', userId),
      where('tipo', '==', 'ganho'),
      where('createdAt', '>=', inicioTimestamp),
      where('createdAt', '<=', fimTimestamp)
    );

    const querySnapshot = await getDocs(q);
    let pontosHoje = 0;

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      pontosHoje += data.pontos || 0;
    });

    return { success: true, pontos: pontosHoje };
  } catch (error) {
    console.error('❌ Erro ao obter pontos ganhos hoje:', error);
    return { success: false, pontos: 0, error: error.message };
  }
};

/**
 * Calcula quantos pontos o usuário pode usar (múltiplos de 100)
 * @param {number} pontosDisponiveis - Pontos disponíveis do usuário
 * @returns {number} - Quantidade máxima de pontos que pode usar
 */
export const calcularPontosUsaveis = (pontosDisponiveis) => {
  if (!pontosDisponiveis || pontosDisponiveis < PONTOS_PARA_REAL) {
    return 0;
  }
  
  // Arredondar para baixo para o múltiplo de 100 mais próximo
  return Math.floor(pontosDisponiveis / PONTOS_PARA_REAL) * PONTOS_PARA_REAL;
};

/**
 * Converte pontos em desconto em reais
 * @param {number} pontos - Quantidade de pontos
 * @returns {number} - Valor do desconto em reais
 */
export const pontosParaDesconto = (pontos) => {
  return pontos / PONTOS_PARA_REAL;
};

/**
 * Converte valor em reais para pontos
 * @param {number} valor - Valor em reais
 * @returns {number} - Quantidade de pontos
 */
export const valorParaPontos = (valor) => {
  return Math.floor(valor * PONTOS_POR_REAL_PADRAO);
};

